import React, { forwardRef } from 'react';
import { ShippingCalendarConfig, getWeekRows, findMark, MarkType, getHolidayName, toIsoDate, findExtraLabel } from './config';

const DAY_LABELS_KO = ['일', '월', '화', '수', '목', '금', '토'];

interface Props {
  config: ShippingCalendarConfig;
  onCellClick?: (date: string) => void;
  capture?: boolean;
}

function dayColor(dayIndex: number): string {
  if (dayIndex === 0) return 'text-red-400';
  if (dayIndex === 6) return 'text-blue-400';
  return 'text-[#6b6b6b]';
}

function numberColor(isHoliday: boolean, dayIdx: number): string {
  if (isHoliday) return 'text-red-500';
  return dayColor(dayIdx);
}

function ShipIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="11" cy="11" r="9" fill="currentColor" />
    </svg>
  );
}

function NoShipIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="5" y1="5" x2="17" y2="17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="17" y1="5" x2="5" y2="17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function PartialIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="11" cy="11" r="9" stroke="currentColor" strokeWidth="2" fill="none" />
      <path d="M 11 2 A 9 9 0 0 1 11 20 Z" fill="currentColor" />
    </svg>
  );
}

const CalendarPoster = forwardRef<HTMLDivElement, Props>(function CalendarPoster({ config, onCellClick, capture = false }, ref) {
  const weeks = getWeekRows(config.startDate, config.endDate);
  const todayIso = toIsoDate(new Date());

  return (
    <div ref={ref} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 w-[800px]">
      {/* NOTICE pill */}
      <div className="flex justify-center">
        <span className="inline-block px-5 py-1.5 border border-jeju-brown rounded-full text-base font-semibold text-jeju-brown tracking-widest">NOTICE</span>
      </div>

      {/* Title */}
      <h2 className="mt-5 text-5xl font-bold text-[#6b6b6b] text-center">{config.title}</h2>

      {/* Divider */}
      <div className="mt-7 border-t border-gray-200" />

      {/* Subtitle */}
      <p className="mt-7 text-xl font-normal text-[#6b6b6b] leading-relaxed whitespace-pre-line text-center">{config.subtitle}</p>

      {/* Legend */}
      <div className="mt-6 flex justify-center">
        <div className="inline-flex items-center gap-6 text-lg text-gray-600 border border-gray-200 rounded-full px-6 py-2">
          <span className="inline-flex items-center gap-1.5">
            <span className="text-jeju-brown"><ShipIcon /></span> 정상 출고
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="text-jeju-brown"><PartialIcon /></span> 부분 출고
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="text-red-500"><NoShipIcon /></span> 출고 없음
          </span>
        </div>
      </div>

      {/* Calendar */}
      <div className="mt-6">
        <div className="grid grid-cols-7 text-center">
          {DAY_LABELS_KO.map((label, i) => (
            <div key={label} className={`py-1 text-xl font-semibold tracking-wider ${dayColor(i)}`}>{label}</div>
          ))}
        </div>
        <div className="mt-4 space-y-5">
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 bg-jeju-brown/5 rounded-2xl">
              {week.map((iso, di) => {
                const mark = findMark(config.marks, iso);
                const type = mark?.type ?? null;
                const dayNumber = Number(iso.slice(8, 10));
                const isToday = todayIso === iso;
                const clickable = Boolean(onCellClick);
                const extraLabel = findExtraLabel(config.extraLabels, iso)?.label;
                const holidayName = extraLabel ?? getHolidayName(iso);
                const isHoliday = !!holidayName;
                // 부분출고 시 유저 라벨 우선, 그 외엔 (추가 라벨 ?? 자동 공휴일) 표시
                const shownLabel = type === 'partial'
                  ? (mark?.label || '')
                  : (holidayName || '');

                return (
                  <button
                    key={iso}
                    type="button"
                    onClick={clickable ? () => onCellClick!(iso) : undefined}
                    disabled={!clickable}
                    className={`flex flex-col items-center gap-1 py-2 rounded-xl ${clickable ? 'hover:bg-jeju-brown/10 cursor-pointer' : 'cursor-default'} ${isToday && clickable && !capture ? 'ring-1 ring-jeju-brown-light' : ''}`}
                  >
                    <span className={`text-3xl font-medium ${numberColor(isHoliday, di)}`}>{dayNumber}</span>
                    <div className="h-8 flex items-center justify-center">
                      {type === 'ship' && <span className="text-jeju-brown"><ShipIcon /></span>}
                      {type === 'noship' && <span className="text-red-500"><NoShipIcon /></span>}
                      {type === 'partial' && <span className="text-jeju-brown"><PartialIcon /></span>}
                    </div>
                    <div className="h-7 flex items-center">
                      {shownLabel && (
                        <span className={`text-lg text-center leading-tight whitespace-nowrap ${type === 'partial' ? 'text-jeju-brown' : (isHoliday || type === 'noship' ? 'text-red-500' : 'text-gray-600')} font-medium`}>{shownLabel}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <p className="pt-5 mt-10 border-t border-gray-100 text-xl text-gray-400 text-center whitespace-pre-line">
        {config.footerMessage}
      </p>
    </div>
  );
});

export default CalendarPoster;
